/**
 * @fileoverview localStorage-backed client mimicking the data-client subset used by the app.
 * @description
 * Exposes `createLocalClient()` returning `{ from(table), auth }` whose surface
 * matches the parts of the data client the services call, so existing service code
 * keeps working unchanged. All data is persisted to browser localStorage.
 *
 * @author Liraz Amir
 * @created 2026-06-11
 */

import type {
  AuthChangeEvent,
  AuthResponse,
  AuthUser,
  LocalQueryResult,
  LocalSession,
} from './local-client.types';

const TABLE_PREFIX = 'aiemd_db_';
const SESSION_KEY = 'aiemd_auth_session';

function readTable<T = any>(table: string): T[] {
  try {
    const raw = localStorage.getItem(TABLE_PREFIX + table);
    return raw ? (JSON.parse(raw) as T[]) : [];
  } catch {
    return [];
  }
}

function writeTable<T = any>(table: string, rows: T[]): void {
  localStorage.setItem(TABLE_PREFIX + table, JSON.stringify(rows));
}

/** uuid without external deps; not cryptographically critical here. */
function uuid(): string {
  const c = crypto as any;
  if (c.randomUUID) {
    return c.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const arr = new Uint8Array(1);
    crypto.getRandomValues(arr);
    const r = arr[0] % 16;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function nowIso(): string {
  return new Date().toISOString();
}

interface Filter {
  op: 'eq' | 'neq' | 'in' | 'lt' | 'lte' | 'gt' | 'gte' | 'like' | 'ilike';
  col: string;
  val: any;
}

/** Convert a SQL LIKE pattern (with % / _ wildcards) to a RegExp. */
function likeToRegExp(pattern: string, caseInsensitive: boolean): RegExp {
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const body = '^' + escaped.replace(/%/g, '.*').replace(/_/g, '.') + '$';
  return new RegExp(body, caseInsensitive ? 'i' : '');
}

/** Chainable query builder mimicking the data-client subset used by the app. */
class LocalQueryBuilder<T = any> implements PromiseLike<LocalQueryResult<T>> {
  private filters: Filter[] = [];
  private orderCol: string | null = null;
  private orderAsc = true;
  private limitN: number | null = null;
  private mode: 'select' | 'insert' | 'update' | 'upsert' | 'delete' = 'select';
  private payload: any = null;
  private upsertOnConflict: string | null = null;
  private wantSingle: 'single' | 'maybe' | null = null;

  constructor(private table: string) {}

  select(_cols?: string): this {
    return this;
  }
  insert(rows: any): this {
    this.mode = 'insert';
    this.payload = rows;
    return this;
  }
  update(patch: any): this {
    this.mode = 'update';
    this.payload = patch;
    return this;
  }
  upsert(rows: any, opts?: { onConflict?: string }): this {
    this.mode = 'upsert';
    this.payload = rows;
    this.upsertOnConflict = opts?.onConflict ?? 'id';
    return this;
  }
  delete(): this {
    this.mode = 'delete';
    return this;
  }
  eq(col: string, val: any): this {
    this.filters.push({ op: 'eq', col, val });
    return this;
  }
  neq(col: string, val: any): this {
    this.filters.push({ op: 'neq', col, val });
    return this;
  }
  in(col: string, val: any[]): this {
    this.filters.push({ op: 'in', col, val });
    return this;
  }
  lt(col: string, val: any): this {
    this.filters.push({ op: 'lt', col, val });
    return this;
  }
  lte(col: string, val: any): this {
    this.filters.push({ op: 'lte', col, val });
    return this;
  }
  gt(col: string, val: any): this {
    this.filters.push({ op: 'gt', col, val });
    return this;
  }
  gte(col: string, val: any): this {
    this.filters.push({ op: 'gte', col, val });
    return this;
  }
  like(col: string, pattern: string): this {
    this.filters.push({ op: 'like', col, val: pattern });
    return this;
  }
  ilike(col: string, pattern: string): this {
    this.filters.push({ op: 'ilike', col, val: pattern });
    return this;
  }
  order(col: string, opts?: { ascending?: boolean }): this {
    this.orderCol = col;
    this.orderAsc = opts?.ascending !== false;
    return this;
  }
  limit(n: number): this {
    this.limitN = n;
    return this;
  }
  single(): this {
    this.wantSingle = 'single';
    return this;
  }
  maybeSingle(): this {
    this.wantSingle = 'maybe';
    return this;
  }

  private matches(row: any): boolean {
    return this.filters.every((f) => {
      const cell = row[f.col];
      switch (f.op) {
        case 'eq':
          return cell === f.val;
        case 'neq':
          return cell !== f.val;
        case 'in':
          return (f.val as any[]).includes(cell);
        case 'lt':
          return cell < f.val;
        case 'lte':
          return cell <= f.val;
        case 'gt':
          return cell > f.val;
        case 'gte':
          return cell >= f.val;
        case 'like':
          return cell != null && likeToRegExp(String(f.val), false).test(String(cell));
        case 'ilike':
          return cell != null && likeToRegExp(String(f.val), true).test(String(cell));
        default:
          return true;
      }
    });
  }

  private run(): LocalQueryResult<T> {
    let rows = readTable(this.table);

    if (this.mode === 'insert' || this.mode === 'upsert') {
      const incoming = Array.isArray(this.payload) ? this.payload : [this.payload];
      const stamped = incoming.map((r) => ({
        id: r.id ?? uuid(),
        created_at: r.created_at ?? nowIso(),
        updated_at: nowIso(),
        ...r,
      }));
      if (this.mode === 'upsert') {
        const key = this.upsertOnConflict || 'id';
        for (const rec of stamped) {
          const idx = rows.findIndex((x: any) => x[key] === (rec as any)[key]);
          if (idx >= 0) {
            rows[idx] = { ...rows[idx], ...rec, updated_at: nowIso() };
          } else {
            rows.push(rec);
          }
        }
      } else {
        rows.push(...stamped);
      }
      writeTable(this.table, rows);
      return this.shape(stamped);
    }

    if (this.mode === 'update') {
      const updated: any[] = [];
      rows = rows.map((r: any) => {
        if (this.matches(r)) {
          const next = { ...r, ...this.payload, updated_at: nowIso() };
          updated.push(next);
          return next;
        }
        return r;
      });
      writeTable(this.table, rows);
      return this.shape(updated);
    }

    if (this.mode === 'delete') {
      const kept = rows.filter((r: any) => !this.matches(r));
      writeTable(this.table, kept);
      return this.shape([]);
    }

    // select
    let result = rows.filter((r: any) => this.matches(r));
    if (this.orderCol) {
      const col = this.orderCol;
      result.sort((a: any, b: any) =>
        a[col] === b[col]
          ? 0
          : a[col] > b[col]
            ? this.orderAsc
              ? 1
              : -1
            : this.orderAsc
              ? -1
              : 1,
      );
    }
    if (this.limitN != null) {
      result = result.slice(0, this.limitN);
    }
    return this.shape(result);
  }

  private shape(rows: any[]): LocalQueryResult<T> {
    if (this.wantSingle === 'single') {
      if (rows.length === 0) {
        return { data: null, error: { message: 'No rows found', code: 'PGRST116' } };
      }
      return { data: rows[0], error: null };
    }
    if (this.wantSingle === 'maybe') {
      return { data: rows.length ? rows[0] : null, error: null };
    }
    return { data: rows as any, error: null };
  }

  then<R1 = LocalQueryResult<T>, R2 = never>(
    onfulfilled?: ((value: LocalQueryResult<T>) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((reason: any) => R2 | PromiseLike<R2>) | null,
  ): PromiseLike<R1 | R2> {
    try {
      return Promise.resolve(this.run()).then(onfulfilled, onrejected);
    } catch (e: any) {
      return Promise.resolve({
        data: null,
        error: { message: e?.message ?? 'error' },
      } as any).then(onfulfilled, onrejected);
    }
  }
}

async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

interface AuthRecord {
  id: string;
  email: string;
  password_hash: string; // dev-grade: SHA-256(salt + password). NOT production secure.
  salt: string;
  user_metadata: Record<string, any>;
  created_at: string;
}

type AuthListener = (event: AuthChangeEvent, session: LocalSession | null) => void;

/** Local credential store replacing the data client auth (the used subset). */
class LocalAuth {
  private listeners: AuthListener[] = [];

  private loadSession(): LocalSession | null {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      return raw ? (JSON.parse(raw) as LocalSession) : null;
    } catch {
      return null;
    }
  }

  private saveSession(session: LocalSession | null): void {
    if (session) {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(SESSION_KEY);
    }
  }

  private emit(event: AuthChangeEvent, session: LocalSession | null): void {
    this.listeners.forEach((l) => l(event, session));
  }

  private toAuthUser(rec: AuthRecord): AuthUser {
    return {
      id: rec.id,
      email: rec.email,
      user_metadata: rec.user_metadata,
      created_at: rec.created_at,
    };
  }

  private makeSession(user: AuthUser): LocalSession {
    return { access_token: 'local-' + user.id, user, expires_at: Date.now() + 86400000 };
  }

  async signUp(credentials: {
    email: string;
    password: string;
    options?: { data?: Record<string, any>; emailRedirectTo?: string };
  }): Promise<AuthResponse<{ user: AuthUser | null; session: LocalSession | null }>> {
    const records = readTable<AuthRecord>('auth_users');
    if (records.some((r) => r.email === credentials.email)) {
      return {
        data: { user: null, session: null },
        error: { message: 'User already registered' },
      };
    }
    const salt = uuid();
    const rec: AuthRecord = {
      id: uuid(),
      email: credentials.email,
      salt,
      password_hash: await sha256(salt + credentials.password),
      user_metadata: credentials.options?.data ?? {},
      created_at: nowIso(),
    };
    records.push(rec);
    writeTable('auth_users', records);
    const user = this.toAuthUser(rec);
    const session = this.makeSession(user);
    this.saveSession(session);
    this.emit('SIGNED_IN', session);
    return { data: { user, session }, error: null };
  }

  async signInWithPassword(credentials: {
    email: string;
    password: string;
  }): Promise<AuthResponse<{ user: AuthUser | null; session: LocalSession | null }>> {
    const records = readTable<AuthRecord>('auth_users');
    const rec = records.find((r) => r.email === credentials.email);
    if (!rec) {
      return {
        data: { user: null, session: null },
        error: { message: 'Invalid login credentials' },
      };
    }
    const hash = await sha256(rec.salt + credentials.password);
    if (hash !== rec.password_hash) {
      return {
        data: { user: null, session: null },
        error: { message: 'Invalid login credentials' },
      };
    }
    const user = this.toAuthUser(rec);
    const session = this.makeSession(user);
    this.saveSession(session);
    this.emit('SIGNED_IN', session);
    return { data: { user, session }, error: null };
  }

  async signOut(): Promise<AuthResponse<null>> {
    this.saveSession(null);
    this.emit('SIGNED_OUT', null);
    return { data: null, error: null };
  }

  async resetPasswordForEmail(_email: string, _opts?: any): Promise<AuthResponse<{}>> {
    // Local mode: no email transport. Treat as success so the UI flow completes.
    return { data: {}, error: null };
  }

  async updateUser(attrs: {
    password?: string;
    data?: Record<string, any>;
  }): Promise<AuthResponse<{ user: AuthUser | null }>> {
    const session = this.loadSession();
    if (!session) {
      return { data: { user: null }, error: { message: 'Not authenticated' } };
    }
    const records = readTable<AuthRecord>('auth_users');
    const idx = records.findIndex((r) => r.id === session.user.id);
    if (idx < 0) {
      return { data: { user: null }, error: { message: 'User not found' } };
    }
    if (attrs.password) {
      records[idx].salt = uuid();
      records[idx].password_hash = await sha256(records[idx].salt + attrs.password);
    }
    if (attrs.data) {
      records[idx].user_metadata = { ...records[idx].user_metadata, ...attrs.data };
    }
    writeTable('auth_users', records);
    const user = this.toAuthUser(records[idx]);
    this.saveSession({ ...session, user });
    return { data: { user }, error: null };
  }

  async getSession(): Promise<AuthResponse<{ session: LocalSession | null }>> {
    return { data: { session: this.loadSession() }, error: null };
  }

  async getUser(): Promise<AuthResponse<{ user: AuthUser | null }>> {
    return { data: { user: this.loadSession()?.user ?? null }, error: null };
  }

  async refreshSession(): Promise<AuthResponse<{ session: LocalSession | null }>> {
    return { data: { session: this.loadSession() }, error: null };
  }

  async resend(_args: { type: string; email: string }): Promise<AuthResponse<{}>> {
    return { data: {}, error: null };
  }

  onAuthStateChange(cb: AuthListener): {
    data: { subscription: { unsubscribe: () => void } };
  } {
    this.listeners.push(cb);
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            this.listeners = this.listeners.filter((l) => l !== cb);
          },
        },
      },
    };
  }
}

export interface LocalClient {
  from<T = any>(table: string): LocalQueryBuilder<T>;
  rpc(fn: string, params?: Record<string, any>): Promise<LocalQueryResult>;
  auth: LocalAuth;
}

let singletonAuth: LocalAuth | null = null;

export function createLocalClient(): LocalClient {
  if (!singletonAuth) {
    singletonAuth = new LocalAuth();
  }
  const auth = singletonAuth;
  return {
    from<T = any>(table: string) {
      return new LocalQueryBuilder<T>(table);
    },
    rpc(fn: string, params?: Record<string, any>): Promise<LocalQueryResult> {
      // Local equivalent of the audit-logging DB function.
      if (fn === 'log_audit_event' && params) {
        return Promise.resolve(
          new LocalQueryBuilder('audit_logs').insert({
            user_id: params['p_user_id'],
            user_email: params['p_user_email'],
            action_type: params['p_action_type'],
            table_name: params['p_table_name'],
            record_id: params['p_record_id'],
            ip_address: params['p_ip_address'],
            user_agent: params['p_user_agent'],
            changes: params['p_changes'],
            metadata: params['p_metadata'],
            access_reason: params['p_access_reason'],
          }),
        );
      }
      // Unknown functions are no-ops in local mode.
      return Promise.resolve({ data: null, error: null });
    },
    auth,
  };
}

/**
 * The single shared local-client instance for the whole app.
 *
 * Both the imperative `dataService` (src/services/data/data.service.ts) and the
 * reactive `useDataStore` (src/stores/data.store.ts) reference THIS instance, so
 * there is exactly one client/auth created at module load (no split-brain). Tests
 * may still call `createLocalClient()` directly to get an isolated client.
 */
export const localClient: LocalClient = createLocalClient();

export { LocalQueryBuilder, LocalAuth };
